import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Mail, Lock } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { AuthOtpInput } from '@features/auth/components/AuthOtpInput';
import * as Sentry from '@sentry/react';
import { loginWithPassword } from '@core/auth/authApi';
import { usePasswordlessAuth } from '../hooks/usePasswordlessAuth';
import { useGoogleSignIn } from '../hooks/useGoogleSignIn';
import { useFacebookLogin } from '../hooks/useFacebookLogin';
import { detectInAppBrowser } from '@shared/utils/inAppBrowser';
import { redirectWithToken as performAuthRedirectWithToken } from '../utils/redirectWithToken';
import { buildOAuthSignupPath, type OAuthSignupLocationState } from '../utils/oauthSignupUrl';
import { putOAuthPrefill } from '../utils/oauthPrefillStore';
import type { OAuthPrefill } from '../types';

/**
 * Standalone login page for auth.v2.meitre.com.
 *
 * Reads context from URL params:
 * - return_url: where to redirect after auth
 * - restaurant: subdomain for API calls
 * - lang: language code
 * - flow: login | signup
 *
 * Post-auth: redirects to return_url?authToken=JWT&user_id=ID
 */
export function AuthLoginPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const returnUrl = searchParams.get('return_url') || '';
  const restaurant = searchParams.get('restaurant') || '';
  const providerHint = searchParams.get('provider') as 'google' | 'facebook' | null;
  const { isInAppBrowser } = detectInAppBrowser();

  useEffect(() => {
    if (isInAppBrowser) {
      Sentry.setTag('browser.webview', detectInAppBrowser().source || 'generic');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Redirect back to the restaurant app with auth credentials */
  const redirectWithToken = useCallback(
    async (token: string, email: string) => {
      await performAuthRedirectWithToken({
        token,
        email,
        restaurant,
        returnUrl,
        successMessage: t('MAGIC_LINK_CODE_SENT'),
      });
    },
    [returnUrl, restaurant, t],
  );

  // Password login state
  const [passwordStep, setPasswordStep] = useState(false);
  const [pwEmail, setPwEmail] = useState('');
  const [pwPassword, setPwPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');

  const pw = usePasswordlessAuth({
    restaurant,
    onSuccess: redirectWithToken,
  });

  /**
   * Route to the signup form with the OAuth profile prefilled. The payload
   * (including the provider uid, which is credential-equivalent server-side)
   * is stored in an in-memory module — only an opaque handle crosses
   * navigation boundaries. That keeps the uid out of the URL, out of the
   * DOM's persisted history.state, and off any copy-shared link or server
   * log. Handles auto-expire after ~10 min if the user abandons the flow.
   *
   * `replace: true` collapses the intermediate /login entry so Back from
   * /signup skips past it instead of retriggering the provider hint that
   * lives in this URL.
   */
  const handleNeedsSignup = useCallback(
    (prefill: OAuthPrefill) => {
      const state: OAuthSignupLocationState = {
        prefillHandle: putOAuthPrefill(prefill),
        origin: 'login',
      };
      navigate(buildOAuthSignupPath(searchParams), { state, replace: true });
    },
    [navigate, searchParams],
  );

  const google = useGoogleSignIn({
    onSuccess: redirectWithToken,
    onNeedsSignup: handleNeedsSignup,
  });

  const facebook = useFacebookLogin({
    onSuccess: redirectWithToken,
    onNeedsSignup: handleNeedsSignup,
  });

  const providerTriggered = useRef(false);
  useEffect(() => {
    if (providerTriggered.current || !providerHint || isInAppBrowser) return;
    if (providerHint === 'google' && google.isReady) {
      providerTriggered.current = true;
      google.signIn();
    } else if (providerHint === 'facebook' && facebook.isReady) {
      providerTriggered.current = true;
      facebook.signIn();
    }
  }, [providerHint, google.isReady, facebook.isReady, google.signIn, facebook.signIn]);

  // Preserve all query params when switching to signup
  const signupSearch = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    return params.toString();
  }, [searchParams]);

  const handlePasswordLogin = useCallback(async () => {
    setPwError('');
    setPwLoading(true);
    try {
      const res = await loginWithPassword({ username: pwEmail, password: pwPassword });
      await redirectWithToken(res.token, pwEmail);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      setPwError(msg || t('MAGIC_LINK_GENERIC_ERROR'));
    } finally {
      setPwLoading(false);
    }
  }, [pwEmail, pwPassword, restaurant, redirectWithToken, t]);

  const handleBack = useCallback(() => {
    if (passwordStep) {
      setPasswordStep(false);
      setPwError('');
      return;
    }
    if (pw.step === 'initial') {
      if (returnUrl) {
        window.location.href = returnUrl;
      } else {
        navigate(-1);
      }
    } else {
      pw.goBack();
    }
  }, [passwordStep, pw, returnUrl, navigate]);

  const renderInitialStep = () => (
    <>
      <h1 className="text-3xl font-display mb-2 text-center">
        {t('SIGNIN_BOOKING_TITLE')}
      </h1>
      <p className="text-foreground/50 text-sm mb-8 text-center max-w-xs">
        {t('SIGNIN_BOOKING_SUBTITLE')}
      </p>

      <div className="w-full max-w-xs flex flex-col gap-3 mb-10">
        {/* Email (passwordless OTP) — primary path. Universal, frames the
            screen as booking-confirmation rather than account-gate. */}
        <Button
          className="py-6 flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02]"
          onClick={pw.goToEmail}
          type="button"
          aria-label={t('SIGNIN_CONTINUE_WITH_EMAIL')}
        >
          <Mail className="w-5 h-5 mr-2" />
          {t('SIGNIN_CONTINUE_WITH_EMAIL')}
        </Button>

        {!isInAppBrowser && (
          <>
            {/* Divider */}
            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 h-px bg-border" />
              <span className="text-foreground/40 text-xs uppercase">{t('SIGNUP_OR')}</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Google Sign-In — convenience shortcut */}
            <Button
              variant="outline"
              className="py-6 flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02]"
              onClick={google.signIn}
              disabled={google.isLoading || !google.isReady}
              type="button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {t('SIGNIN_CONTINUE_WITH_GOOGLE')}
            </Button>

            {/* Facebook Login — convenience shortcut */}
            <Button
              variant="outline"
              className="py-6 flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02]"
              onClick={facebook.signIn}
              disabled={facebook.isLoading || !facebook.isReady}
              type="button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              {t('SIGNUP_CONTINUE_WITH_FACEBOOK')}
            </Button>
          </>
        )}
      </div>

      {!isInAppBrowser && (google.error || facebook.error) && (
        <p className="text-red-500 text-sm text-center mb-4">
          {google.error || facebook.error}
        </p>
      )}

      <div className="w-full text-center">
        <button
          className="text-foreground/50 text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 rounded"
          onClick={() => setPasswordStep(true)}
          type="button"
        >
          <Lock className="w-3.5 h-3.5 inline mr-1" />
          {t('LOGIN_WITH_PASSWORD')}
        </button>
      </div>
    </>
  );

  const renderEmailStep = () => (
    <>
      <h1 className="text-3xl font-display mb-2 text-center">
        {t('MAGIC_LINK_TITLE')}
      </h1>
      <p className="text-foreground/50 text-sm mb-8 text-center">
        {t('MAGIC_LINK_SUBTITLE')}
      </p>

      <form
        className="w-full max-w-xs flex flex-col gap-4 mb-10"
        onSubmit={(e) => {
          e.preventDefault();
          pw.sendCode();
        }}
      >
        <Input
          type="email"
          placeholder={t('MAGIC_LINK_EMAIL_PLACEHOLDER')}
          value={pw.email}
          onChange={(e) => pw.setEmail(e.target.value)}
          autoFocus
          aria-label={t('SIGNUP_LABEL_EMAIL')}
          className="py-6"
        />

        {pw.error && (
          <p className="text-red-500 text-sm text-center">{pw.error}</p>
        )}

        <Button
          type="submit"
          className="py-6 transition-all duration-200 hover:scale-[1.02]"
          disabled={pw.isSending || !pw.email}
        >
          {pw.isSending ? t('MAGIC_LINK_SENDING') : t('MAGIC_LINK_SEND_CODE')}
        </Button>
      </form>
    </>
  );

  const renderVerificationStep = () => (
    <>
      <h1 className="text-3xl font-display mb-2 text-center">
        {t('MAGIC_LINK_CHECK_EMAIL')}
      </h1>
      <p className="text-foreground/50 text-sm mb-6 text-center">
        {t('MAGIC_LINK_VERIFY_SUBTITLE')}
      </p>
      <p className="text-foreground font-medium text-sm mb-8 text-center">
        {pw.email}
      </p>

      <div className="w-full max-w-xs flex flex-col items-center gap-4 mb-10">
        <AuthOtpInput
          value={pw.otp}
          onChange={pw.handleOtpChange}
          disabled={pw.isVerifying}
        />

        {/* Fixed-height status row prevents layout shift when "Verifying..."
            or an error toggles in/out of the DOM. */}
        <div className="min-h-5 flex items-center" aria-live="polite">
          {pw.isVerifying && (
            <p className="text-foreground/50 text-sm">{t('MAGIC_LINK_VERIFY_BUTTON')}</p>
          )}
          {pw.error && (
            <p className="text-red-500 text-sm text-center">{pw.error}</p>
          )}
        </div>

        <div className="flex flex-col items-center gap-2 mt-4">
          <p className="text-foreground/50 text-sm">
            {t('MAGIC_LINK_DIDNT_RECEIVE')}
          </p>
          <button
            type="button"
            className="text-brand font-medium text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 rounded disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={pw.resendCode}
            disabled={pw.resendCooldown > 0 || pw.isSending}
          >
            {pw.resendCooldown > 0
              ? t('MAGIC_LINK_RESEND_IN', { seconds: pw.resendCooldown })
              : t('MAGIC_LINK_RESEND_CODE')}
          </button>
          <button
            type="button"
            className="text-foreground/50 text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 rounded"
            onClick={() => pw.goBack()}
          >
            {t('MAGIC_LINK_CHANGE_EMAIL')}
          </button>
        </div>
      </div>
    </>
  );

  const renderPasswordStep = () => (
    <>
      <h1 className="text-3xl font-display mb-2 text-center">
        {t('SIGNIN_TITLE')}
      </h1>
      <p className="text-foreground/50 text-sm mb-8 text-center">
        {t('SIGNIN_PASSWORD_SUBTITLE')}
      </p>

      <form
        className="w-full max-w-xs flex flex-col gap-4 mb-10"
        onSubmit={(e) => {
          e.preventDefault();
          handlePasswordLogin();
        }}
      >
        <Input
          type="email"
          placeholder={t('SIGNUP_LABEL_EMAIL')}
          value={pwEmail}
          onChange={(e) => setPwEmail(e.target.value)}
          autoFocus
          className="py-6"
        />
        <Input
          type="password"
          placeholder={t('FORM_LABEL_PASSWORD')}
          value={pwPassword}
          onChange={(e) => setPwPassword(e.target.value)}
          className="py-6"
        />

        {pwError && (
          <p className="text-red-500 text-sm text-center">{pwError}</p>
        )}

        <Button
          type="submit"
          className="py-6 transition-all duration-200 hover:scale-[1.02]"
          disabled={pwLoading || !pwEmail || !pwPassword}
        >
          {pwLoading ? t('MAGIC_LINK_VERIFY_BUTTON') : t('SIGNIN_TITLE')}
        </Button>
      </form>

      <button
        className="text-foreground/50 text-sm hover:underline focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 rounded"
        onClick={() => {
          const params = new URLSearchParams(searchParams);
          navigate(`/forgot-password?${params.toString()}`);
        }}
        type="button"
      >
        {t('NEW_MODAL_FORGOT_PASSWORD')}
      </button>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-0 lg:p-4 bg-background">
      <div className="w-full lg:max-w-md bg-card shadow-none rounded-b-2xl lg:shadow-lg lg:rounded-2xl overflow-visible lg:overflow-hidden animate-fade-in border-t border-border/30">
        <main className="px-6 pt-8 pb-10 flex flex-col items-center">
          {passwordStep && renderPasswordStep()}
          {!passwordStep && pw.step === 'initial' && renderInitialStep()}
          {!passwordStep && pw.step === 'email' && renderEmailStep()}
          {!passwordStep && pw.step === 'verification' && renderVerificationStep()}
        </main>

        {/* Hide the back-button footer on the initial step — its only
            destination is wherever the user came from (often the landing
            or an external link), which isn't a useful escape hatch and
            undermines the "finish your booking" framing. Keep it on the
            email / verification / password sub-steps. */}
        {(passwordStep || pw.step !== 'initial') && (
          <footer className="border-t border-border px-6 py-4 flex items-center">
            <button
              aria-label="Go back"
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
              onClick={handleBack}
              type="button"
            >
              <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </footer>
        )}
      </div>

      <p className="hidden lg:block mt-6 text-foreground/30 text-xs">
        Powered by{' '}
        <span
          className="font-['Playfair_Display'] font-bold tracking-wide"
          style={{ backgroundImage: 'linear-gradient(to right, #FF3366, #FF3366)', backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent' }}
        >
          Meitre
        </span>
      </p>
    </div>
  );
}

export default AuthLoginPage;
